import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * The form's own state, kept here rather than in either app's global store:
 * the customer app has Redux, the professional app does not, and the form
 * bootstraps itself from the server on every mount, so nothing needs to
 * outlive the page.
 */
const INITIAL = {
  wdDoc: null,
  wdSections: [],
  // The event entries, in the order the server holds them.
  wdEvents: [],
  // The venue catalogue for this booking's city, plus the rows with no city
  // such as "Not decided" and "Bride's home".
  wdVenues: [],
  // Package coverage per date, which decides whether a timing saves or becomes
  // an edit request.
  wdWindows: {},
  // When each day's coverage opens, for the days sold without a closing
  // hour: those carry no window above, but the opening still stands.
  wdOpens: {},
  wdSpan: null,
  wdLocksAt: null,
  wdClosed: false,
  wdWantsInterview: null,
  wdAskInterview: false,
  wdInterviewQuestion: null,
  // Caps the payload carries, such as selected faces per side. The server
  // enforces them; the client shows them as a courtesy.
  wdLimits: {},
  // Per side: the uploaded photo, whether detection is still running, and the
  // face tags the detector returned.
  wdFamily: {},
  wdBookingName: '',
  // Which side of the couple this user is, as the server reads it off the
  // booking; the link never carries it.
  wdRole: null,
  // One side has submitted, so the three shared sections are closed for both.
  wdSharedLocked: false,
  // Who changed what, as the status endpoint reports it. Both sides write the
  // same document, so an edit needs an author beside it.
  wdChangeLog: [],
  wdLoading: true,
  wdSaveState: 'idle',
  wdSection: null,
  // The Schedule section is several stops on one screen. Unset until the
  // couple moves, so the form opens on whichever stop comes first for them.
  wdPhase: null,
  wdActiveDate: null,
  wdDatePage: 0,
  // Only the bride and groom may fill the form. Everyone else on the booking
  // reads it.
  wdCanEdit: true,
  // The brief is not this visitor's to read at all. Distinct from a failed
  // request, which is worth reporting differently.
  wdDenied: false,
  wdDeniedCode: '',
  // The server's own words for the refusal, which name the case the code
  // alone cannot, such as a staff read made as the wrong side.
  wdDeniedMessage: '',
  // The link carries no brief, or one that is malformed or unknown to the
  // server. Nothing renders but the toast saying so.
  wdInvalidLink: false,
  // The form could not be loaded at all: a server error or no connection.
  // Said with a way to try again rather than left as an empty form.
  wdLoadFailed: false,
  // Each side's coordinator and backup contact, as a fixed grid of four.
  wdContacts: [],
  // The events a ritual can be added as, which ops maintains. The same list
  // the booking page offers when an event is added to a booking.
  wdRitualOptions: [],
  // Whether this couple asked for interviews on the creative preferences
  // form. Decides whether the timeline has a third stop.
  wdNeedsInterview: false,
  // One row per side and group, answered or not. The server sends the whole
  // grid, so an unanswered sitting is still a question on screen.
  wdInterviews: [],
  // A refusal that belongs to no one control, such as a ritual the day
  // already holds. Shown as a toast and cleared on dismissal.
  wdToast: '',
  // Server messages that belong against one control, keyed by entry and field,
  // such as a refused timing or a cap that was already full.
  wdFieldErrors: {},
};

const StoreContext = createContext(null);

const setterName = (field) => `set${field.charAt(0).toUpperCase()}${field.slice(1)}`;

export const WeddingDayStore = ({ children }) => {
  const [state, setState] = useState(INITIAL);

  // Setters are built once so they are stable across renders.
  const setters = useMemo(() => {
    const out = {};
    Object.keys(INITIAL).forEach((field) => {
      out[setterName(field)] = (value) =>
        setState((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
    });
    return out;
  }, []);

  const value = useMemo(() => ({ ...state, ...setters }), [state, setters]);

  return React.createElement(StoreContext.Provider, { value }, children);
};

/** Reads every field plus its setter. Fields are named, so callers can destructure. */
export const useWeddingDayStore = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('WeddingDay must be rendered inside WeddingDayStore');
  }
  return store;
};
